import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUp, Check, MessageCircleQuestion } from 'lucide-react';
import { toast } from 'sonner';
import { useDecideApproval } from '../../../hooks/mutations';
import { CardShell, ghostBtn, primaryBtn } from './CardShell';

/**
 * The bot asks and the person answers with a click, like OpenMausBot's question card:
 * the options as one list with letters (A, B, C), one of them (or several), or their
 * own words underneath.
 */
const letter = (i) => String.fromCharCode(65 + (i % 26));
const QuestionCard = ({ approval, botName, compact }) => {
  const { t } = useTranslation();
  const decide = useDecideApproval();
  const card = approval.card;
  const [picked, setPicked] = useState([]);
  const [other, setOther] = useState('');

  if (approval.status !== 'pending') {
    const answers = Array.isArray(approval.answer) ? approval.answer : approval.answer ? [approval.answer] : [];
    return (
      <div className="rounded-2xl bg-bubble-bot/70 px-3.5 py-2.5 space-y-1.5" data-testid="question-card-resolved">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <MessageCircleQuestion className="w-3.5 h-3.5" /><span className="truncate">{card.question}</span>
        </div>
        {approval.status === 'approved' ? (
          <div className="flex flex-wrap gap-1.5">
            {answers.map((a) => (
              <span key={a} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-honey-soft text-[13px] text-amber-800 dark:text-amber-200">
                <Check className="w-3 h-3" />{a}
              </span>
            ))}
          </div>
        ) : <div className="text-xs text-muted-foreground">{t('bots.cards.skipped')}</div>}
      </div>
    );
  }

  const busy = decide.isPending;
  const answer = (value) => decide.mutate({ approvalId: approval.id, approve: true, answer: value }, {
    onError: (err) => toast.error(err?.response?.data?.detail || t('bots.cards.failed')),
  });
  const skip = () => decide.mutate({ approvalId: approval.id, approve: false });
  const toggle = (label) => setPicked((p) => (p.includes(label) ? p.filter((x) => x !== label) : [...p, label]));
  const sendMultiple = () => {
    const all = [...picked, ...(other.trim() ? [other.trim()] : [])];
    if (all.length) answer(all);
  };

  return (
    <CardShell icon={MessageCircleQuestion} testid="question-card" waiting compact={compact} large={!compact}
      eyebrow={t('bots.cards.question_eyebrow', { bot: botName })} title={card.question}
      footer={(
        <>
          <span className="flex-1" />
          <button type="button" className={ghostBtn} onClick={skip} disabled={busy} data-testid="question-skip">
            {t('bots.cards.skip')}
          </button>
          {card.multiple && (
            <button type="button" className={primaryBtn} onClick={sendMultiple} disabled={busy || (!picked.length && !other.trim())}
              data-testid="question-send">
              {t('bots.cards.send_answer')}
            </button>
          )}
        </>
      )}>
      <div className="space-y-2.5">
        <div className="rounded-xl border border-border overflow-hidden divide-y divide-border">
          {card.options.map((o, i) => {
            const on = picked.includes(o.label);
            return (
              <button key={o.label} type="button" disabled={busy} data-testid="question-option"
                onClick={() => (card.multiple ? toggle(o.label) : answer(o.label))}
                className={`group w-full flex items-center gap-3 text-left px-3.5 py-2.5 transition-colors disabled:opacity-50 ${on
                  ? 'bg-amber-500/10' : 'hover:bg-accent/60'}`}>
                <span className={`w-5 h-5 flex-shrink-0 rounded-md text-[11px] font-semibold flex items-center justify-center ${on
                  ? 'bg-amber-500 text-honey-ink' : 'bg-accent text-muted-foreground group-hover:text-foreground'}`}>
                  {card.multiple && on ? <Check className="w-3 h-3" /> : letter(i)}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm text-foreground">{o.label}</span>
                  {o.description && <span className="block text-xs text-muted-foreground">{o.description}</span>}
                </span>
              </button>
            );
          })}
        </div>
        {card.allow_other && (
          <form className="flex items-center gap-2 rounded-xl bg-background pl-3.5 pr-1.5 py-1.5 border border-transparent focus-within:border-amber-500/50"
            onSubmit={(e) => { e.preventDefault(); if (card.multiple) sendMultiple(); else if (other.trim()) answer(other.trim()); }}>
            <input value={other} onChange={(e) => setOther(e.target.value)} maxLength={500} disabled={busy}
              placeholder={t('bots.cards.other_placeholder')} data-testid="question-other"
              className="flex-1 min-w-0 h-8 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none" />
            {!card.multiple && (
              <button type="submit" disabled={busy || !other.trim()} aria-label={t('bots.cards.send_answer')}
                className="w-7 h-7 rounded-lg bg-amber-500 text-honey-ink flex items-center justify-center disabled:opacity-30">
                <ArrowUp className="w-3.5 h-3.5" />
              </button>
            )}
          </form>
        )}
      </div>
    </CardShell>
  );
};

export default QuestionCard;
